import React, { useEffect, useMemo, useState } from 'react';
import BsModal from '../Common/BsModal';
import { useConfig } from '../../DataStore/ConfigContext';
import Notification from '../Common/Notification';
import ajaxVoucher from '../../util/remote/ajaxVoucher';

const INITIAL_FORM = { plan_id: '', router_id: '', qty: 1, expires_in_hours: 0, note: '' };

const NewVoucher = ({ show, onClose, onSuccess, selection = null, vendor=false, title }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const { wifiPlans, getWifiPlans, routers, getRouters, selectedRouter } = useConfig();
  const [loading, setLoading] = useState(false);
  const [notify, setNotify] = useState(null);

  // Normalize selection -> plan id (supports id string/number or plan object)
  const selectionId = useMemo(() => {
    if (!selection) return null;
    return String(typeof selection === 'object' ? selection.id : selection);
  }, [selection]);

  // Routers to show: prefer full list; fall back to selectedRouter only
  const routersToRender = useMemo(() => {
    if (Array.isArray(routers) && routers.length) return routers;
    return selectedRouter ? [selectedRouter] : [];
  }, [routers, selectedRouter]);

  // Fetch plans/routers when opened, reset when closed
  useEffect(() => {
    if (show) {
      getWifiPlans?.(true);
      getRouters?.(true);
      setForm((f) => ({
        ...INITIAL_FORM,
        plan_id: selectionId ?? '',
        // Preselect router from context if available
        router_id: selectedRouter?.id ? String(selectedRouter.id) : '',
      }));
    } else {
      setForm(INITIAL_FORM);
      setNotify(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, selectionId, selectedRouter?.id]);

  // If plans load after mount and selection exists, ensure plan_id is set
  useEffect(() => {
    if (show && selectionId && !form.plan_id) {
      setForm((f) => ({ ...f, plan_id: selectionId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wifiPlans, show, selectionId]);

  // If routers load later and there's only one, auto-select it (unless already set)
  useEffect(() => {
    if (show && !form.router_id && routersToRender.length === 1 && routersToRender[0]?.id) {
      setForm((f) => ({ ...f, router_id: String(routersToRender[0].id) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routersToRender, show]);

  const handleSubmit = async () => {
    if (loading) return;
    if (!form.plan_id || form.qty < 1) {
      setNotify(<Notification type="danger" message="Please select a plan and enter a valid quantity." />);
      return;
    }
    if (!form.router_id) {
      setNotify(<Notification type="danger" message="Please select a router." />);
      return;
    }

    setLoading(true);
    const server_response = vendor ? await ajaxVoucher.sellVoucher(form) : await ajaxVoucher.createVoucher(form);
    setLoading(false);

    if (server_response.status === 'OK') {
      setNotify(<Notification type="success" message={server_response.message} />);
      setTimeout(() => onSuccess(), 1000);
    } else {
      setNotify(<Notification type="danger" message={server_response.message} />);
    }
  };

  const plansToRender = useMemo(() => {
    if (!Array.isArray(wifiPlans)) return [];
    if (selectionId) {
      const found = wifiPlans.filter((p) => String(p.id) === selectionId);
      return found.length ? found : [{ id: selectionId, name: 'Selected Plan' }];
    }
    return wifiPlans;
  }, [wifiPlans, selectionId]);

  const renderPlanOptions = () =>
    plansToRender.map((plan) => (
      <option key={plan.id} value={plan.id}>
        {plan.name}
      </option>
    ));

  const renderRouterOptions = () =>
    routersToRender.map((r) => (
      <option key={r.id} value={r.id}>
        {r.name || `Router #${r.id}`}{r.site ? ` — ${r.site}` : ''}
      </option>
    ));

  return (
    <BsModal
      show={show}
      onClose={onClose}
      title={title || "New Manual Voucher"}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit}>
            <i className="fa fa-check-circle mr-1" />
            {loading ? ' Generating...' : ' Generate'}
          </button>
        </>
      }
    >
      {notify}

      {/* Router select */}
     {!vendor && <div className="form-group">
        <label>Router</label>
        <select
          className="form-control"
          value={form.router_id}
          onChange={(e) => setForm((f) => ({ ...f, router_id: e.target.value }))}
        >
          <option value="">Select router…</option>
          {renderRouterOptions()}
        </select>
        {selectedRouter?.id && (
          <small className="text-muted d-block mt-1">Preselected from current router context.</small>
        )}
      </div>}

      {/* Plan select */}
      <div className="form-group">
        <label>Wifi Plan</label>
        <select
          className="form-control"
          value={form.plan_id}
          onChange={(e) => setForm((f) => ({ ...f, plan_id: e.target.value }))}
        >
          {!selectionId && <option value="">Select plan…</option>}
          {renderPlanOptions()}
        </select>
        {selectionId && (
          <small className="text-muted d-block mt-1">
            Preselected from previous step.
          </small>
        )}
      </div>

      {/* Qty + Expiry */}
      <div className="form-row">
        <div className="form-group col-md-6">
          <label>Quantity</label>
          <input
            type="number"
            min={1}
            className="form-control"
            value={form.qty}
            onChange={(e) => setForm((f) => ({ ...f, qty: e.target.value }))}
          />
        </div>
        {!vendor && <div className="form-group col-md-6">
          <label>Expires In (hours)</label>
          <input
            type="number"
            min={0}
            className="form-control"
            value={form.expires_in_hours}
            onChange={(e) =>
              setForm((f) => ({ ...f, expires_in_hours: Math.max(0, +e.target.value) }))
            }
          />
          <small className="text-muted">0 = plan default</small>
        </div>}
      </div>

      {/* Note */}
      <div className="form-group mb-0">
        <label>Note (optional)</label>
        <input
          type="text"
          className="form-control"
          value={form.note}
          onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
        />
      </div>
    </BsModal>
  );
};

export default NewVoucher;
